import React, { useLayoutEffect, useState } from 'react';
import styled from 'styled-components';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import { VideoGalleryIframe, VideoPreviewDialog } from '@clatter/ui';
import { useDeepCompareMemo } from '@clatter/platform';

const StyledVideoGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  grid-auto-rows: minmax(${(props) => `${props.videoSize.height + 48}px`}, auto);
  gap: ${({ theme }) => theme.videoGallery?.gap};
  width: auto;
  height: auto;
  overflow-y: auto;
  justify-content: center;
  overflow-x: hidden;

  @media (min-width: 1060px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1400px) {
    grid-template-columns: ${(props) => `repeat(${props.numOfColumns ?? 3}, 1fr)`};
  }

  .video-gallery-item {
    margin-bottom: auto;

    .video {
      width: ${(props) => `${props.videoSize.width}px`};
      height: ${(props) => `${props.videoSize.height}px`};
      position: relative;
      background-color: #000;

      iframe {
        width: 100%;
        height: 100%;
      }
    }

    .preview-video-button {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;

      svg {
        font-size: 18px;
        border-radius: 8px;
        background-color: rgba(0, 0, 0, 0.9);
        fill: #fff;
        width: 48px;
        height: 36px;
      }
    }

    .video-details {
      padding: ${({ theme }) => theme.videoGallery?.item.padding};
      max-width: ${(props) => `${props.videoSize.width}px`};

      .video-title {
        font-family: ${({ theme }) => theme.videoGallery?.item.title.fontFamily};
        font-weight: ${({ theme }) => theme.videoGallery?.item.title.fontWeight};
        font-size: ${({ theme }) => theme.videoGallery?.item.title.fontSize};
        line-height: ${({ theme }) => theme.videoGallery?.item.title.lineHeight};
        color: ${({ theme }) => theme.videoGallery?.item.title.color};
        padding: ${({ theme }) => theme.videoGallery?.item.title.padding};
        /* white-space: nowrap; */
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .video-description {
        font-size: ${({ theme }) => theme.videoGallery?.item.description.fontSize};
        color: ${({ theme }) => theme.videoGallery?.item.description.color};
      }
    }

    @media (min-width: 350px) {
      margin: auto;
      margin-top: 0;
    }
  }
`;

const RenderVideos = ({ videos, showDescription, onVideoClick, urlProperty }) => {
  if (!videos || !videos.length) {
    return <p>No videos</p>;
  }

  return videos.map((video) => (
    <div className="video-gallery-item" key={video?.id}>
      <div className="video">
        <div
          data-id={video?.id}
          data-video-name={video?.title} // required by GA4 do not remove
          onClick={onVideoClick}
          className="preview-video-button"
          data-clatter-event={JSON.stringify({
            type: 'Video Click',
            data: {
              name: video?.title,
            },
          })}
        >
          <PlayArrowIcon />
        </div>
        {<VideoGalleryIframe videoUrl={video?.[urlProperty]} />}
      </div>
      <div className="video-details">
        <div className="video-title">{video?.title}</div>
        {showDescription && (
          <div className="video-description">{video?.description || '(description not provided)'}</div>
        )}
      </div>
    </div>
  ));
};

const VideoGallery = ({
  showDescription = false,
  urlProperty = 'link',
  videos = [],
  videoWidth = 200,
  videoHeight: customVideoHeight,
  videoAspectRatio = '16/9',
  numOfColumns,
}) => {
  const [videoSize, setVideoSize] = useState(null);
  const [previewVideo, setPreviewVideo] = useState(null);

  useLayoutEffect(() => {
    const [widthRatio, heightRatio] = videoAspectRatio.split('/');
    const videoHeight = customVideoHeight
      ? customVideoHeight
      : parseInt((videoWidth / Number(widthRatio)) * Number(heightRatio));
    setVideoSize({ width: videoWidth, height: videoHeight });
  }, [videoWidth, videoAspectRatio]);

  const handleVideoClick = (event) => {
    if (!event) {
      // clicked outside video container, closing dialog window
      return setPreviewVideo(null);
    }

    const clickedItemId = parseInt(event?.currentTarget?.dataset?.id);
    const nextPreviewVideo = (videos || []).find((video) => video.id === clickedItemId) || null;

    setPreviewVideo(nextPreviewVideo);
  };

  const tabbedVideos = useDeepCompareMemo(() => {
    const tabsSet = new Set(); // store unique tab names
    const subcategoryMap = {}; // { [tab name]: [videos] }
    let hasSubcategories = false;

    videos.forEach((video) => {
      const [category, subcategory] = (video?.video_category?.categoryName || '').split('.');

      // if no subcategory is provided, fall back to category name as tab
      const tabName = subcategory || category;

      if (subcategory) {
        hasSubcategories = true;
      }

      if (!subcategoryMap[tabName]) {
        subcategoryMap[tabName] = [];
        tabsSet.add(tabName);
      }

      subcategoryMap[tabName].push(video);
    });

    // if none of the videos have subcategories, just return them as-is
    if (!hasSubcategories) {
      return {
        tabs: [],
        noSubcategoryVideos: videos,
      };
    }

    return {
      tabs: Array.from(tabsSet),
      subcategoryMap,
    };
  }, [videos]);

  if (!videoSize) {
    return null;
  }

  return (
    <>
      {tabbedVideos?.tabs?.length > 0 ? (
        <Tabs
          data={tabbedVideos}
          videoSize={videoSize}
          urlProperty={urlProperty}
          numOfColumns={numOfColumns}
          showDescription={showDescription}
          onVideoClick={handleVideoClick}
        />
      ) : (
        <StyledVideoGallery videoSize={videoSize} numOfColumns={numOfColumns}>
          <RenderVideos
            urlProperty={urlProperty}
            showDescription={showDescription}
            videos={tabbedVideos.noSubcategoryVideos}
            onVideoClick={handleVideoClick}
          />
        </StyledVideoGallery>
      )}

      <VideoPreviewDialog
        title={previewVideo?.title}
        videoUrl={previewVideo?.link}
        open={!!previewVideo}
        onCloseDialog={handleVideoClick}
      />
    </>
  );
};

export default VideoGallery;

const TabsWrapper = styled.div`
  padding: 2rem;
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  max-width: 304px;
  margin: auto;

  @media (min-width: 425px) {
    max-width: 350px;
    margin: 0 auto;
  }

  @media (min-width: 768px) {
    max-width: 668px;
    margin: 0 auto;
  }

  @media (min-width: 1024px) {
    max-width: 960px;
  }
`;

const TabHeaderList = styled.div`
  display: flex;
  overflow-x: auto;
  max-width: 100%;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 51, 102, 0.3);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme?.videoGallery?.tabs?.backgroundColor};
  }
`;

const TabButton = styled.button`
  background-color: ${({ isActive, theme }) => (isActive ? theme?.videoGallery?.tabs?.backgroundColor : 'transparent')};
  border: none;
  padding: 1rem 1.5rem;
  font-size: 1.25rem;
  font-weight: bold;
  color: ${({ theme }) => theme?.videoGallery?.tabs?.textColor};
  cursor: pointer;
`;

const TabContent = styled.div`
  background-color: ${({ theme }) => theme?.videoGallery?.tabs?.backgroundColor};
  padding: 4rem 2rem;
`;

const TabHeaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  overflow: hidden;
`;

const Tabs = ({ data, onVideoClick, urlProperty, showDescription, numOfColumns, videoSize }) => {
  const [activeTab, setActiveTab] = useState(data.tabs[0]);

  return (
    <TabsWrapper>
      <TabHeaderWrapper>
        <TabHeaderList>
          {data.tabs.map((tab) => (
            <TabButton key={tab} isActive={activeTab === tab} onClick={() => setActiveTab(tab)}>
              {tab}
            </TabButton>
          ))}
        </TabHeaderList>
      </TabHeaderWrapper>

      <TabContent>
        <StyledVideoGallery videoSize={videoSize} numOfColumns={numOfColumns}>
          <RenderVideos
            showDescription={showDescription}
            urlProperty={urlProperty}
            videos={data.subcategoryMap[activeTab]}
            onVideoClick={onVideoClick}
          />
        </StyledVideoGallery>
      </TabContent>
    </TabsWrapper>
  );
};
